/**
 * Una pieza de banner: la imagen con sus medidas y, si lleva enlace, el `<Link>`
 * que la envuelve. La comparten las tres filas de la portada (carrusel,
 * promociones y cuadros), que solo cambian lo que le escriben encima.
 *
 * Es un `<Link>` de verdad y no un `onClick`: el banner es una ruta del
 * catalogo que se puede abrir en otra pestana (SEO-01).
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { Banner } from '@elcarpintero/compartido';

import { sizesDe, srcSetDe } from '../../utilidades/imagen-responsiva';
import { Imagen } from '../comunes/Imagen';

export interface LaminaBannerProps {
  banner: Banner;
  /** Solo para la imagen que domina la primera pantalla (LCP). */
  prioridad?: boolean;
  /** Ancho con que se ve la pieza (`sizes`), segun la fila que la muestra. */
  tamanos: string;
  /** Lo que se dibuja sobre la foto: titulo, bajada, boton. */
  children?: ReactNode;
}

/** El mismo corte que `carrusel.css` usa para el telefono. */
const MEDIA_TELEFONO = '(max-width:720px)';

export function LaminaBanner({
  banner,
  prioridad = false,
  tamanos,
  children,
}: LaminaBannerProps) {
  const imagen = (
    <Imagen
      src={banner.imagenUrl}
      alt={banner.textoAlternativo}
      ancho={banner.ancho}
      alto={banner.alto}
      prioridad={prioridad}
      variantes={banner.variantes}
      tamanos={tamanos}
    />
  );
  const movil = banner.imagenMovil;

  // Con pieza para el telefono, el navegador baja solo una de las dos.
  const contenido = (
    <>
      {movil !== null ? (
        <picture>
          <source
            media={MEDIA_TELEFONO}
            {...(movil.variantes.length > 0
              ? { sizes: sizesDe(tamanos, !prioridad) }
              : {})}
            srcSet={srcSetDe(movil.url, movil.ancho, movil.variantes) ?? movil.url}
            width={movil.ancho}
            height={movil.alto}
          />
          {imagen}
        </picture>
      ) : (
        imagen
      )}
      {children}
    </>
  );

  if (banner.enlace === null) return <div className="lamina-enlace">{contenido}</div>;

  return (
    <Link to={banner.enlace} className="lamina-enlace">
      {contenido}
    </Link>
  );
}
